import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { AppState } from '../../reducers';
import { Store } from '@ngrx/store';
import { NotificationService } from '../_services';
import {
  CancelNotificationsPage,
  ClearNotificationError,
  CreateNotification,
  CreateNotificationOnServer,
  DeleteNotification,
  DeleteNotificationOnServer,
  LoadNotificationsPage,
  NotificationActionTypes,
  RequestNotificationsPage,
  ThrowNotificationError,
  ToggleNotificationActionLoading,
  ToggleNotificationsPageLoading,
  UpdateNotification,
  UpdateNotificationOnServer,
} from '../_actions/notification.actions';

@Injectable()
export class NotificationEffects {
  showPageLoading = new ToggleNotificationsPageLoading({ pageLoading: true });
  hidePageLoading = new ToggleNotificationsPageLoading({ pageLoading: false });
  showActionLoading = new ToggleNotificationActionLoading({ actionLoading: true });
  hideActionLoading = new ToggleNotificationActionLoading({ actionLoading: false });

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private notificationService: NotificationService,
  ) {}

  @Effect()
  loadNotificationsPage$ = this.actions$.pipe(
    ofType<RequestNotificationsPage>(NotificationActionTypes.RequestNotificationsPage),
    map((action: RequestNotificationsPage) => action.payload.query),
    mergeMap((query) => {
      this.store.dispatch(this.showPageLoading);
      const queryResult$ = this.notificationService.getNotificationsPage(query);
      const observables = [queryResult$, of(query)];

      return forkJoin(observables).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.length) {
            this.store.dispatch(new ClearNotificationError());
            this.store.dispatch(this.hidePageLoading);

            return new LoadNotificationsPage({
              notifications: res[0].data,
              totalCount: res[0].count,
              query: res[1],
            });
          }

          this.store.dispatch(new CancelNotificationsPage());

          return new ThrowNotificationError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  createNotification$ = this.actions$.pipe(
    ofType<CreateNotificationOnServer>(NotificationActionTypes.CreateNotificationOnServer),
    map((action: CreateNotificationOnServer) => action.payload.notification),
    mergeMap((notification) => {
      this.store.dispatch(this.showActionLoading);

      return this.notificationService.createNotification(notification).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 201) {
            this.store.dispatch(new ClearNotificationError());
            this.store.dispatch(this.hideActionLoading);

            return new CreateNotification({ notification: res });
          }

          return new ThrowNotificationError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  updateNotification$ = this.actions$.pipe(
    ofType<UpdateNotificationOnServer>(NotificationActionTypes.UpdateNotificationOnServer),
    map((action: UpdateNotificationOnServer) => action.payload.notification),
    mergeMap((notification) => {
      this.store.dispatch(this.showActionLoading);

      return this.notificationService.updateNotification(notification).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            this.store.dispatch(new ClearNotificationError());
            this.store.dispatch(this.hideActionLoading);

            return new UpdateNotification({ notification: res });
          }

          return new ThrowNotificationError({ error: res });
        }),
      );
    }),
  );

  @Effect()
  deleteNotification$ = this.actions$.pipe(
    ofType<DeleteNotificationOnServer>(NotificationActionTypes.DeleteNotificationOnServer),
    map((action: DeleteNotificationOnServer) => action.payload.id),
    mergeMap((id) => {
      this.store.dispatch(this.showActionLoading);

      return this.notificationService.deleteNotification(id).pipe(
        catchError((err) => of(err)),
        map((res) => {
          if (res.status === 200) {
            this.store.dispatch(new ClearNotificationError());
            this.store.dispatch(this.hideActionLoading);

            return new DeleteNotification({ id });
          }

          return new ThrowNotificationError({ error: res });
        }),
      );
    }),
  );
}
